// Firebase configuration and initialization
import { initializeApp } from 'firebase/app';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  connectAuthEmulator,
  type User 
} from 'firebase/auth';
import { 
  initializeFirestore,
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  updateDoc, 
  serverTimestamp,
  connectFirestoreEmulator,
  query,
  orderBy,
  limit
} from 'firebase/firestore';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate config
const requiredKeys = ['apiKey', 'authDomain', 'projectId'] as const;
for (const key of requiredKeys) {
  if (!firebaseConfig[key]) {
    console.error(`Missing Firebase config: ${key}. Check your .env.local file.`);
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  // More robust over VPN / proxied networks where QUIC/WebChannel can flap.
  experimentalAutoDetectLongPolling: true,
});
export let analytics: Analytics | null = null;

// Initialize analytics only when supported (browser, cookies, etc.)
if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
  isSupported()
    .then((supported) => {
      if (supported) {
        analytics = getAnalytics(app);
      }
    })
    .catch(() => {
      // Ignore analytics initialization failures in unsupported environments.
    });
}

// Connect to emulators in development
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099');
  connectFirestoreEmulator(db, 'localhost', 8081);
  console.log('🔧 Connected to Firebase emulators');
}

// Auth providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// ============================================================================
// Auth Functions
// ============================================================================

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

export const signInWithEmail = async (email: string, password: string) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email.trim(), password);
    return result.user;
  } catch (error) {
    console.error('Email sign-in error:', error);
    throw error;
  }
};

export const logOut = () => signOut(auth);

// ============================================================================
// User Profile Functions
// ============================================================================

export interface UserProfile {
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  createdAt: Date;
  tier: 'free' | 'pro' | 'enterprise';
}

export async function createOrUpdateUserProfile(user: User): Promise<void> {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    // Create new user profile
    await setDoc(userRef, {
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      createdAt: serverTimestamp(),
      tier: 'free',
    });
  } else {
    // Update last login
    await updateDoc(userRef, {
      lastLogin: serverTimestamp(),
    });
  }
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) return null;
  
  const data = userSnap.data();
  return {
    email: data.email,
    displayName: data.displayName,
    photoURL: data.photoURL,
    createdAt: data.createdAt?.toDate() || new Date(),
    tier: data.tier || 'free',
  };
}

// ============================================================================
// API Key Management
// ============================================================================

export interface ApiKey {
  id?: string;
  key: string;
  name: string;
  createdAt: Date;
  active: boolean;
  lastUsed?: Date;
}

function generateApiKey(): string {
  const chars = 'abcdef0123456789';
  let result = 'qk_live_';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export async function createApiKey(userId: string, name: string): Promise<ApiKey> {
  const keysRef = collection(db, 'users', userId, 'api_keys');
  
  // Check existing key count
  const existingKeys = await getDocs(keysRef);
  if (existingKeys.size >= 3) {
    throw new Error('Maximum API keys reached (3). Delete an existing key first.');
  }
  
  const newKey: Omit<ApiKey, 'id'> = {
    key: generateApiKey(),
    name: name.trim(),
    createdAt: new Date(),
    active: true,
  };
  
  const docRef = await addDoc(keysRef, {
    ...newKey,
    createdAt: serverTimestamp(),
  });
  
  return { ...newKey, id: docRef.id };
}

export async function getUserApiKeys(userId: string): Promise<ApiKey[]> {
  const keysRef = collection(db, 'users', userId, 'api_keys');
  const q = query(keysRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    key: doc.data().key,
    name: doc.data().name,
    active: doc.data().active ?? true,
    createdAt: doc.data().createdAt?.toDate() || new Date(),
    lastUsed: doc.data().lastUsed?.toDate(),
  }));
}

export async function deleteApiKey(userId: string, keyId: string): Promise<void> {
  const keyRef = doc(db, 'users', userId, 'api_keys', keyId);
  await deleteDoc(keyRef);
}

export async function toggleApiKey(userId: string, keyId: string, active: boolean): Promise<void> {
  const keyRef = doc(db, 'users', userId, 'api_keys', keyId);
  await updateDoc(keyRef, { active });
}

// ============================================================================
// Saved Request Templates
// ============================================================================

export interface SavedRequest {
  id?: string;
  name: string;
  productType: string;
  request: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

export async function saveRequestTemplate(
  userId: string, 
  template: Omit<SavedRequest, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const templatesRef = collection(db, 'users', userId, 'saved_requests');
  
  const docRef = await addDoc(templatesRef, {
    ...template,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  
  return docRef.id;
}

export async function getSavedRequests(userId: string): Promise<SavedRequest[]> {
  const templatesRef = collection(db, 'users', userId, 'saved_requests');
  const q = query(templatesRef, orderBy('updatedAt', 'desc'), limit(50));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    name: doc.data().name,
    productType: doc.data().productType,
    request: doc.data().request,
    createdAt: doc.data().createdAt?.toDate() || new Date(),
    updatedAt: doc.data().updatedAt?.toDate() || new Date(),
  }));
}

export async function deleteSavedRequest(userId: string, requestId: string): Promise<void> {
  const requestRef = doc(db, 'users', userId, 'saved_requests', requestId);
  await deleteDoc(requestRef);
}

// Re-export auth observer
export { onAuthStateChanged };
export type { User };
